import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MobileShell from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Stethoscope } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await register(form.name, form.email, form.password);
        toast.success("Welcome. Your physiotherapist will assign your program.");
        navigate("/dashboard");
      } else {
        const u = await login(form.email, form.password);
        toast.success("Signed in");
        navigate(u.role === "patient" ? "/dashboard" : u.role === "therapist" ? "/my-patients" : "/admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileShell>
      <div className="px-6 pt-10">
        <Link to="/" className="flex items-center gap-2 text-teal-700" data-testid="back-home">
          <Stethoscope size={20} />
          <span className="text-xs font-bold uppercase tracking-[0.2em]">Bexley Road Physio</span>
        </Link>
        <h1 className="mt-6 text-3xl font-bold font-display text-slate-900">
          {mode === "register" ? "Create your account" : "Welcome back"}
        </h1>
        <p className="mt-2 text-slate-600 text-sm">
          {mode === "register"
            ? "Takes 30 seconds. Your data stays private to you and the clinic."
            : "Sign in with your email or staff username."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="mt-1 h-12 rounded-xl"
              />
            </div>
          )}
          <div>
            <Label htmlFor="email">{mode === "register" ? "Email" : "Email or username"}</Label>
            <Input
              id="email"
              type={mode === "register" ? "email" : "text"}
              data-testid="auth-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              autoCapitalize="none"
              autoComplete={mode === "register" ? "email" : "username"}
              className="mt-1 h-12 rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              className="mt-1 h-12 rounded-xl"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {loading ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
          </Button>
        </form>

        <button
          type="button"
          data-testid="auth-toggle"
          onClick={() => setMode(mode === "register" ? "login" : "register")}
          className="block mx-auto mt-6 text-sm text-slate-500 hover:text-teal-600"
        >
          {mode === "register" ? "I already have an account" : "Create a new account"}
        </button>
      </div>
    </MobileShell>
  );
}
